import { Form, TimePicker } from "antd";
import dayjs from "dayjs";
import { nanoid } from "nanoid";
import { AppointmentTimeSlotPlanItemFormValues } from "./AppointmentTimeSlotPlanForm";
import { useEffect } from "react";
import { Modal } from "@/components/v2";
import {
  Button,
  Card,
  CardFooter,
  InputNumber,
  Select,
} from "@rumbledirect/ui";

interface AppointmentTimeSlotPlanItemModalProps {
  open: boolean;
  onCancel: () => void;
  onSubmit: (item: any) => void;
  selectedItem?: AppointmentTimeSlotPlanItemFormValues | null;
}

export const AppointmentTimeSlotPlanItemModal = ({
  open,
  onCancel,
  onSubmit,
  selectedItem,
}: AppointmentTimeSlotPlanItemModalProps) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      const values = selectedItem;
      let initialValues = {};
      if (values) {
        initialValues = {
          ...values,
          startTime: values?.startTime
            ? dayjs(values.startTime, "HH:mm")
            : null,
          endTime: values?.endTime ? dayjs(values.endTime, "HH:mm") : null,
        };
      }
      form.setFieldsValue(initialValues);
    }
  }, [open, form, selectedItem]);

  const handleOk = async (values: AppointmentTimeSlotPlanItemFormValues) => {
    const data = {
      ...values,
      startTime:
        typeof values.startTime === "string"
          ? values.startTime
          : values.startTime?.format("HH:mm") || null,
      endTime:
        typeof values.endTime === "string"
          ? values.endTime
          : values.endTime?.format("HH:mm") || null,
    };

    if (!data._key) {
      data._key = nanoid();
    }

    form.resetFields();
    onSubmit(data);
  };

  return (
    <Modal open={open} onCancel={onCancel}>
      <Form form={form} layout="vertical" onFinish={handleOk}>
        <Card title={selectedItem ? "Tijdslot bewerken" : "Nieuw tijdslot"}>
          <Form.Item hidden name="_key" />
          <Form.Item
            name="dayOfWeek"
            label="Dag"
            rules={[{ required: true, message: "Dit veld is verplicht" }]}
          >
            <Select
              options={[
                { label: "Maandag", value: "monday" },
                { label: "Dinsdag", value: "tuesday" },
                { label: "Woensdag", value: "wednesday" },
                { label: "Donderdag", value: "thursday" },
                { label: "Vrijdag", value: "friday" },
                { label: "Zaterdag", value: "saturday" },
                { label: "Zondag", value: "sunday" },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="startTime"
            label="Starttijd"
            rules={[{ required: true, message: "Dit veld is verplicht" }]}
          >
            <TimePicker
              format="HH:mm"
              minuteStep={5}
              placeholder="Selecteer tijd"
              needConfirm={false}
              showNow={false}
              className="w-full"
            />
          </Form.Item>

          <Form.Item
            name="endTime"
            label="Eindtijd"
            dependencies={["startTime"]}
            rules={[
              { required: true, message: "Dit veld is verplicht" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const start = getFieldValue("startTime");
                  if (!start || !value) return Promise.resolve();
                  if (value.isAfter(start)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("Eindtijd moet na starttijd liggen"),
                  );
                },
              }),
            ]}
          >
            <TimePicker
              placeholder="Selecteer tijd"
              format="HH:mm"
              minuteStep={5}
              className="w-full"
              needConfirm={false}
              showNow={false}
            />
          </Form.Item>

          <Form.Item
            name="amount"
            label="Aantal sloten"
            rules={[{ required: true, message: "Dit veld is verplicht" }]}
          >
            <InputNumber min={0} className="w-full" />
          </Form.Item>

          <CardFooter>
            <Button onClick={onCancel} className="mr-2">
              Annuleren
            </Button>
            <Button type="primary" onClick={() => form.submit()}>
              Opslaan
            </Button>
          </CardFooter>
        </Card>
      </Form>
    </Modal>
  );
};

export default AppointmentTimeSlotPlanItemModal;
